import React, { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../../layouts/MainLayout";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  faEdit,
  faEye,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import ListHeader from "../../../../components/ListHeader";
import useAction from "../../../../hooks/useAction";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useGenericFilter from "../../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../../components/AutoCompleteFilter";
import baseService from "../../../../services/baseService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import FilterForm from "../../../../components/FilterForm";
import useFilterHandlers from "../../../../hooks/useFilterHandlers";
import TableWithInactiveFilter from "../../../../components/table/TableWithInactiveFilter";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const LocationCustomerPage = () => {
  // Hooks de contexto e navegação
  const { canAccess } = usePermissions();
  const { id, addressId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Hooks personalizados para funcionalidades gerais
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);
  const queryClient = useQueryClient();

  // --- Gerenciamento de Filtros de URL (PADRÃO UnitPage) ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      area: [],
      section: [],
      spot: [],
      deleted_at: false,
    },
    "location",
  );

  // --- Callbacks para o useGenericFilter (PADRÃO UnitPage) ---
  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  // --- Gerenciamento de Filtros Locais do Formulário (PADRÃO UnitPage) ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        area: [],
        section: [],
        spot: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  // --- Query para buscar a lista de Localizações (PADRÃO UnitPage) ---
  const {
    data: locationsResponseData,
    isLoading: isLoadingLocations,
    isFetching: isFetchingLocations,
    isError: isErrorLocations,
    error: locationsQueryError,
  } = useQueryWithLoader({
    queryKey: ["customerLocations", id, addressId, filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(
        entities.customers.customerAddressLocations(id, addressId),
        params,
      );
      return response.result;
    },
    enabled: !!id && !!addressId,
    staleTime: 5 * 60 * 1000,
    select: (data) => ({
      locations: data.data.map((loc) => ({
        id: loc.id || "-",
        area: loc.area || "-",
        section: loc.section || "-",
        spot: loc.spot || "-",
        status: !loc.deleted_at ? "Ativo" : "Inativo",
        inRequest: loc.in_request,
        inTransaction: loc.in_transaction,
        isDeleted: !!loc.deleted_at,
      })),
      totalPages: data.last_page,
    }),
  });

  const columns = [
    { key: "area", label: "Área" },
    { key: "section", label: "Seção" },
    { key: "spot", label: "Ponto" },
  ];

  const { locations = [], totalPages = 1 } = locationsResponseData || {};

  // --- Gerenciamento de Loader com base nos estados do TanStack Query (PADRÃO UnitPage) ---
  useEffect(() => {
    if (isLoadingLocations || isFetchingLocations) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingLocations, isFetchingLocations, showLoader, hideLoader]);

  // --- Notificação de Erro para a Query de Localizações (PADRÃO UnitPage) ---
  useEffect(() => {
    if (isErrorLocations) {
      console.error(
        "LocationCustomerPage: Erro ao carregar localizações (useQuery):",
        locationsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar localizações: ${
          locationsQueryError.response?.data?.message ||
          locationsQueryError.message
        }`,
      );
    }
  }, [isErrorLocations, locationsQueryError, showNotification]);

  // --- Gerenciamento de Modal de Confirmação e Ações (PADRÃO UnitPage) ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  // --- Função para Forçar o Refetch da Lista (PADRÃO UnitPage) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["customerLocations", id, addressId],
    });
  }, [queryClient, id, addressId]);

  // --- Mutações para Inativar e Ativar Localizações (PADRÃO UnitPage) ---

  const inactiveLocationMutation = useMutation({
    mutationFn: async (locationIdToActivate) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.customers.customerAddressLocationDelete(
          id,
          addressId,
          locationIdToActivate,
        ),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) refreshList();
    },
    onError: (err) => {
      console.error("onError", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation();
    },
  });

  const activeLocationMutation = inactiveLocationMutation;

  // --- Efeito para exibir notificações de estado da rota (PADRÃO UnitPage) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Definição das ações da tabela (PADRÃO UnitPage) ---
  const actions = useMemo(
    () => [
      {
        id: "viewDetails",
        icon: faEye,
        title: "Ver Detalhes",
        buttonClass: "btn-info",
        permission: "Listar endereços de clientes",
        onClick: (loc) =>
          navigate(
            `/aluguel/clientes/${id}/endereco/${addressId}/localizacoes/detalhes/${loc.id}`,
          ),
        show: (loc) =>
          !loc.isDeleted && canAccess("Listar endereços de clientes"),
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar endereços de clientes",
        onClick: (loc) =>
          navigate(
            `/aluguel/clientes/${id}/endereco/${addressId}/localizacoes/editar/${loc.id}`,
          ),
        show: (loc) =>
          !loc.isDeleted &&
          (!loc.inRequest || !loc.inTransaction) &&
          canAccess("Atualizar endereços de clientes"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir endereços de clientes",
        onClick: (loc) =>
          showConfirmation(
            loc,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveLocationMutation.mutateAsync(loc.id);
              } catch (error) {
                console.error("Erro ao excluir localização:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (loc) =>
          !loc.isDeleted &&
          (!loc.inRequest || !loc.inTransaction) &&
          canAccess("Excluir endereços de clientes"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir endereços de clientes",
        onClick: (loc) =>
          showConfirmation(
            loc,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activeLocationMutation.mutateAsync(loc.id);
              } catch (error) {
                console.error("Erro ao restaurar localização:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (loc) =>
          loc.isDeleted && canAccess("Excluir endereços de clientes"),
      },
    ],
    [
      navigate,
      id,
      addressId,
      showConfirmation,
      activeLocationMutation,
      inactiveLocationMutation,
      canAccess,
    ],
  );

  // --- Definição dos inputs para o formulário de filtros (PADRÃO UnitPage) ---
  const inputsfilters = useMemo(
    () => [
      {
        label: "Área",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="area"
            model="location"
            value={localFilterValues.area}
            onChange={(selected) => handleFilterChange("area", selected)}
            placeholder="Filtre pela área"
            isMulti
            isClearable
          />
        ),
      },
      {
        label: "Seção",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="section"
            model="location"
            value={localFilterValues.section}
            onChange={(selected) => handleFilterChange("section", selected)}
            placeholder="Filtre pela seção"
            isMulti
            isClearable
          />
        ),
      },
      {
        label: "Ponto",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="spot"
            model="location"
            value={localFilterValues.spot}
            onChange={(selected) => handleFilterChange("spot", selected)}
            placeholder="Filtre pelo ponto"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Localizações do Cliente"
        showBackButton={true}
        backUrl={`/aluguel/clientes/detalhes/${id}`}
      />
      <div className="container-fluid p-1">
        {/* Formulário de Filtros */}
        <FilterForm
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
          filters={localFilterValues}
        />

        {/* Cabeçalho da Lista de Localizações */}
        <ListHeader
          title="Lista de Localizações"
          buttonText="Nova Localização"
          buttonLink={`/aluguel/clientes/${id}/endereco/${addressId}/localizacoes/adicionar`}
          canAccess={canAccess}
          permission="Criar endereços de clientes"
        />

        {/* Tabela Dinâmica de Localizações */}
        <TableWithInactiveFilter
          data={locations}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoadingLocations}
          isFetching={isFetchingLocations}
        />

        {/* Modal de Confirmação */}
        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            selectedItem
              ? `${selectedItem.area} - ${selectedItem.section} - ${selectedItem.spot}`
              : ""
          }
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default LocationCustomerPage;
